import type {Builder, Pointer, Experiment} from '@wix/document-services-types'
import type {WidgetRepository} from '../repository/WidgetRepository'
import type {ComponentRepository} from '../repository/ComponentRepository'
import {ElementBuilderFactory, BaseElementBuilder} from '../manifestBuilders/elements'
import {NativeActionsBuilder, CustomActionsBuilder} from '../manifestBuilders/actions'
import type {ComponentDataRepository} from '../repository/ComponentDataRepository'
import type {RefComponentRepository} from '../repository/RefComponentRepository'
import {DisplayFiltersBuilder} from '../manifestBuilders/displayFilters'
import type {DataModelAPI} from '../../dataModel/dataModel'

export class ManifestElementsService {
    constructor(
        private readonly widgetRepository: WidgetRepository,
        private readonly componentRepository: ComponentRepository,
        private readonly refComponentRepository: RefComponentRepository,
        private readonly componentDataRepository: ComponentDataRepository,
        private readonly experimentInstance: Experiment,
        private readonly dataModel: DataModelAPI,
        private readonly typeMapper?: (elementType: string, compPointer: Pointer) => string | undefined
    ) {}

    /**
     * Get data and style overrides for a ref component
     * @param refComponentPointer - Pointer to the ref component
     * @returns ElementOverrides - Data and style overrides for the ref component
     */
    getRefComponentsOverrides(refComponentPointer: Pointer): Builder.ElementsDefaults {
        const refComponentData = this.componentDataRepository.getComponentDataItem(refComponentPointer)
        const widgetRootPointer = this.widgetRepository.getWidgetPagePointer(refComponentData.rootCompId)
        const rootContainerPointer = this.widgetRepository.getRootContainerPointerFromRootWidget(widgetRootPointer)
        if (!rootContainerPointer) {
            throw new Error('Ref component container pointer not found')
        }

        const childrenPointers = this.widgetRepository.getAllChildrenPointers(rootContainerPointer)
        return childrenPointers.reduce<Builder.ElementsDefaults>((acc, childPointer) => {
            const componentRole = this.componentRepository.getRoleByWidgetRoot(childPointer, widgetRootPointer)
            const dataOverride = this.refComponentRepository.getDataOverride(refComponentPointer, childPointer)
            // Since data has system fields, we need to normalize it
            const normalizedDataOverride = BaseElementBuilder.toBuilderData(dataOverride)
            return {
                ...acc,
                [componentRole]: {
                    data: normalizedDataOverride
                }
            }
        }, {})
    }

    getElement(
        componentPointer: Pointer,
        widgetRootContainerPointer: Pointer,
        componentRole: string,
        parentPointer: Pointer
    ): Builder.ElementItem | undefined {
        const componentType = this.componentRepository.getComponentType(componentPointer)
        const componentManifest = this.componentRepository.getManifestData(componentPointer)
        const nativeActions = new NativeActionsBuilder(componentType, componentManifest?.gfpp, this.dataModel).build()
        const customActions = new CustomActionsBuilder(componentType, componentManifest?.gfpp, this.dataModel).build()

        const displayFilters = new DisplayFiltersBuilder(componentManifest?.gfpp, componentType).build()

        const componentData = this.componentDataRepository.getComponentDataItem(componentPointer)

        const elementBuilder = new ElementBuilderFactory({
            data: componentData,
            type: componentType,
            role: componentRole,
            actions: nativeActions,
            customActions,
            pointer: componentPointer,
            manifest: componentManifest,
            customTypeMapper: this.typeMapper,
            rootContainerPointer: widgetRootContainerPointer,
            displayFilters,
            parentPointer,
            experimentInstance: this.experimentInstance
        })
        return elementBuilder.create()
    }

    /**
     * Gets manifest elements from a widget root component
     *
     * @param widgetRootPointer - Pointer to component with type AppWidget
     * @returns Builder.Elements - Collection of manifest elements where each element represents
     *                            a child component from the widget's root container
     */
    getManifestElements(widgetRootPointer: Pointer): Builder.Elements {
        const rootContainerPointer = this.widgetRepository.getRootContainerPointerFromRootWidget(widgetRootPointer)
        const childrenPointers = this.widgetRepository.getAllChildrenPointers(widgetRootPointer)
        if (!rootContainerPointer) {
            throw new Error('Root container pointer not found')
        }

        return childrenPointers.reduce((acc, childPointer) => {
            const componentRole = this.componentRepository.getRoleByWidgetRoot(childPointer, widgetRootPointer)
            const element = this.getElement(childPointer, rootContainerPointer, componentRole, widgetRootPointer)
            if (element && componentRole) {
                acc[componentRole] = element
            }
            return acc
        }, {} as Builder.Elements)
    }
}
